import { useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PortfolioResponse } from "../types/portfolio";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";
import { markDirty } from "../lib/dirtyViews";
import { queryKeys } from "../lib/queryKeys";

type UsePortfolioArgs = {
  poolCode: string | null;
  enabled?: boolean;
};

const buildUrl = (poolCode: string | null) => {
  let path = "/api/portfolio";
  if (poolCode) {
    path += `?poolCode=${encodeURIComponent(poolCode)}`;
  }
  return apiUrl(path);
};

const buildCancelUrl = (orderId: number) => apiUrl(`/api/orders/${orderId}/cancel`);
const buildUpdateUrl = (orderId: number) => apiUrl(`/api/orders/${orderId}`);

const parseApiError = async (response: Response, fallback: string) => {
  const text = await response.text();
  if (!text) {
    return fallback;
  }

  try {
    const errorData = JSON.parse(text) as { detail?: unknown; message?: unknown };
    if (errorData.detail && typeof errorData.detail === "object" && "message" in errorData.detail) {
      const detailMessage = (errorData.detail as { message?: unknown }).message;
      if (typeof detailMessage === "string") {
        return detailMessage;
      }
    }
    if (typeof errorData.detail === "string") {
      return errorData.detail;
    }
    if (typeof errorData.message === "string") {
      return errorData.message;
    }
    return text;
  } catch {
    return text;
  }
};

export function usePortfolio({ poolCode, enabled }: UsePortfolioArgs) {
  const isFeatureEnabled = enabled ?? true;
  const { acquireToken } = useApiAuth();
  const { isSignedIn: isAuthenticated } = useUser();
  const queryClient = useQueryClient();

  const canLoad = useMemo(
    () => Boolean(isFeatureEnabled && isAuthenticated && poolCode),
    [isAuthenticated, isFeatureEnabled, poolCode],
  );

  const queryKey = useMemo(() => queryKeys.portfolio(poolCode ?? "none"), [poolCode]);

  const {
    data: portfolio,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey,
    enabled: canLoad,
    queryFn: async () => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required to load portfolio.");
      }

      const response = await fetch(buildUrl(poolCode), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const detail = await parseApiError(
          response,
          `Failed to load portfolio (${response.status})`,
        );
        throw new Error(detail || `Failed to load portfolio (${response.status})`);
      }

      const apiPayload: PortfolioResponse = await response.json();
      return apiPayload;
    },
  });

  const invalidatePortfolio = async () => {
    await queryClient.invalidateQueries({ queryKey });
    await refetch();
  };

  const markPoolViewsDirty = (source: "order_submit" | "order_update") => {
    if (!poolCode) return;
    markDirty(queryClient, "portfolio", poolCode, source);
    markDirty(queryClient, "leaderboard", poolCode, source);
    markDirty(queryClient, "home", poolCode, source);
  };

  const cancelMutation = useMutation({
    mutationFn: async (orderId: number) => {
      if (!isFeatureEnabled) {
        throw new Error("Portfolio feature is disabled.");
      }
      if (!isAuthenticated) {
        throw new Error("Sign in required to cancel orders.");
      }

      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required to cancel orders.");
      }

      const response = await fetch(buildCancelUrl(orderId), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const detail = await parseApiError(
          response,
          `Failed to cancel order (${response.status})`,
        );
        throw new Error(detail || `Failed to cancel order (${response.status})`);
      }
    },
    onSuccess: async () => {
      markPoolViewsDirty("order_update");
      await invalidatePortfolio();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ orderId, price, quantity }: { orderId: number; price: number; quantity: number }) => {
      if (!isFeatureEnabled) {
        throw new Error("Portfolio feature is disabled.");
      }
      if (!isAuthenticated) {
        throw new Error("Sign in required to update orders.");
      }

      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required to update orders.");
      }

      const response = await fetch(buildUpdateUrl(orderId), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ price, quantity }),
      });

      if (!response.ok) {
        const detail = await parseApiError(
          response,
          `Failed to update order (${response.status})`,
        );
        throw new Error(detail || `Failed to update order (${response.status})`);
      }
    },
    onSuccess: async () => {
      markPoolViewsDirty("order_update");
      await invalidatePortfolio();
    },
  });

  return {
    portfolio: portfolio ?? null,
    isLoading,
    isError,
    canLoad,
    reload: refetch,
    cancelOrder: async (orderId: number) => {
      await cancelMutation.mutateAsync(orderId);
    },
    updateOrder: async (orderId: number, price: number, quantity: number) => {
      await updateMutation.mutateAsync({ orderId, price, quantity });
    },
  };
}

export type UsePortfolioResult = ReturnType<typeof usePortfolio>;
